import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Iframe from "react-iframe";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

import BgImg from "../../assets/img/website-images/Discover-your-future-in-pharmacy.webp";

/* ─── Counter data ─────────────────────────────────────────────── */
const counters = [
    { start: 0,    end: 25,   label: "Faculty",   sub: "& Staff"       },
    { start: 0,    end: 120,  label: "Students",  sub: "Enrolled"      },
    { start: 0,    end: 8,    label: "Modern",    sub: "Laboratories"  },
    { start: 2000, end: 2023, label: "Year",      sub: "Established"   },
];

/* ─── Variants ─────────────────────────────────────────────────── */
const videoSectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
    },
};

const videoContentStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.18, delayChildren: 0.25 } },
};

const videoContentChild = {
    hidden: { opacity: 0, y: 25 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
    },
};

const playBtnVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
        opacity: 1, scale: 1,
        transition: { duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay: 0.5 },
    },
};

const counterGridVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};

const counterItemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.92 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
    },
};

/* ─── Single Counter Item ──────────────────────────────────────── */
function CounterItem({ item, inView }) {
    const numRef = useRef(null);

    /* GSAP: number pulse on entry */
    useEffect(() => {
        if (inView && numRef.current) {
            gsap.fromTo(
                numRef.current,
                { scale: 0.7, opacity: 0 },
                { scale: 1, opacity: 1, duration: 0.6, ease: "back.out(1.8)", delay: 0.2 }
            );
        }
    }, [inView]);

    return (
        <motion.div className="col-lg-3 col-md-6 col-sm-12" variants={counterItemVariants}>
            <div className="single-counter">
                <div
                    className="counter p-relative"
                    style={{
                        backgroundImage: "url(assets/img/bg/c-object.png)",
                        backgroundRepeat: "no-repeat",
                    }}
                >
                    <span ref={numRef} style={{ opacity: 0 }}>
                        {inView && (
                            <CountUp
                                className="count"
                                start={item.start}
                                end={item.end}
                                duration={2}
                            />
                        )}
                    </span>
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.45, delay: 0.45 }}
                    >
                        {item.label} <br /> {item.sub}
                    </motion.p>
                </div>
            </div>
        </motion.div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function Aboutvideo() {
    const [video, setVideo] = useState(false);

    /* Video section */
    const { ref: videoRef, inView: videoInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* Counter section */
    const { ref: counterRef, inView: counterInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: play button pulse loop */
    const playRef = useRef(null);
    useEffect(() => {
        if (videoInView && playRef.current) {
            gsap.to(playRef.current, {
                scale: 1.1,
                duration: 0.8,
                ease: "sine.inOut",
                repeat: -1,
                yoyo: true,
                delay: 0.8,
            });
        }
    }, [videoInView]);

    /* GSAP: counter section bg pulse on entry */
    const counterBgRef = useRef(null);
    useEffect(() => {
        if (counterInView && counterBgRef.current) {
            gsap.fromTo(
                counterBgRef.current,
                { backgroundSize: "102%" },
                { backgroundSize: "100%", duration: 0.8, ease: "power2.out" }
            );
        }
    }, [counterInView]);

    return (
        <>
            {/* ══ Video Section ════════════════════════════════════ */}
            <section id="video" className="video-area p-relative">
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <motion.div
                                ref={videoRef}
                                className="s-video-wrap"
                                style={{ backgroundImage: `url(${BgImg})` }}
                                variants={videoSectionVariants}
                                initial="hidden"
                                animate={videoInView ? "visible" : "hidden"}
                            >
                                <motion.div
                                    className="s-video-content text-center"
                                    variants={videoContentStagger}
                                    initial="hidden"
                                    animate={videoInView ? "visible" : "hidden"}
                                >
                                    <motion.h2 variants={videoContentChild}>
                                        Discover Your Future in Pharmacy
                                    </motion.h2>

                                    <motion.p variants={videoContentChild}>
                                        Learn about our modern facilities, experienced faculty, and
                                        industry-focused B.Pharm program designed to prepare
                                        students for successful careers in the pharmaceutical and
                                        healthcare sectors.
                                    </motion.p>

                                    {/* Play button — spring pop + infinite pulse */}
                                    <motion.h6
                                        variants={playBtnVariants}
                                        initial="hidden"
                                        animate={videoInView ? "visible" : "hidden"}
                                    >
                                        <Link
                                            to="#"
                                            className="popup-video mb-50"
                                            onClick={() => setVideo(true)}
                                        >
                                            <img
                                                ref={playRef}
                                                src="/assets/img/bg/play-button2.png"
                                                alt="Play Video"
                                                style={{ display: "inline-block" }}
                                            />
                                        </Link>
                                    </motion.h6>
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ══ Counter Section ══════════════════════════════════ */}
            <div
                ref={(el) => { counterRef(el); counterBgRef.current = el; }}
                className="counter-area pb-90"
                style={{ background: "#fdc110" }}
            >
                <div className="container">
                    <motion.div
                        className="row p-relative"
                        variants={counterGridVariants}
                        initial="hidden"
                        animate={counterInView ? "visible" : "hidden"}
                    >
                        {counters.map((item, i) => (
                            <CounterItem key={i} item={item} inView={counterInView} />
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* ══ Video Modal ═══════════════════════════════════════ */}
            {video && (
                <>
                    <div className="mfp-bg mfp-ready"></div>
                    <div
                        className="mfp-wrap mfp-close-btn-in mfp-auto-cursor mfp-ready"
                        tabIndex="-1"
                        style={{ overflow: "hidden" }}
                    >
                        <div className="mfp-container mfp-s-ready mfp-iframe-holder">
                            <div className="mfp-content">
                                <div className="mfp-iframe-scaler">
                                    <button
                                        title="Close (Esc)"
                                        type="button"
                                        className="mfp-close"
                                        onClick={() => setVideo(false)}
                                    >
                                        ×
                                    </button>
                                    <Iframe
                                        className="mfp-iframe"
                                        src="https://www.youtube.com/embed/loAXdRwP0cA?autoplay=1"
                                        frameBorder="0"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default Aboutvideo;
